import React, { useEffect, useState } from "react";
import { Box, Drawer, Typography } from "@mui/material";
import {
  CalculateRounded,
  HeightRounded,
  MonitorWeightRounded,
} from "@mui/icons-material";

import { InfoTile, tileInputProps } from "../../../molecules";
import { DrawerHeader, DrawerFooter, FormTextField } from "../../../atoms";
import { COLORS, TYPO } from "../../../../styles/uiConstants";
import { calculateBmi, bmiCategory } from "../../../../utils/bmi";

// Boy + kilodan BMI hesaplar; "Kaydet" ile hesaplanan değer hastaya yazılır.
// Boy/kilo saklanmaz — yalnızca BMI kaydedilir.
export default function BmiCalculatorDrawer({ open, onClose, onSave, saving }) {
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");

  useEffect(() => {
    if (open) {
      setHeight("");
      setWeight("");
    }
  }, [open]);

  const bmi = calculateBmi(height, weight);
  const category = bmiCategory(bmi);
  const bothFilled = String(height).trim() !== "" && String(weight).trim() !== "";

  const numericInput = (value, setter, unit) => (
    <FormTextField
      value={value}
      onChange={(e) => setter((e.target.value || "").replace(/[^\d.,]/g, ""))}
      fullWidth
      size="small"
      variant="standard"
      inputProps={{ inputMode: "decimal" }}
      InputProps={{
        ...tileInputProps(true),
        endAdornment: (
          <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate400, ml: 1 }}>
            {unit}
          </Typography>
        ),
      }}
    />
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 440 },
          bgcolor: COLORS.white,
          boxShadow: "0 8px 32px 0 rgba(60,72,100,0.15)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DrawerHeader
        icon={<CalculateRounded />}
        title="BMI Hesapla"
        subtitle="Boy ve kilo girin, vücut kitle indeksi hesaplansın"
        onClose={onClose}
      />

      <Box sx={{ p: { xs: 2, md: 2.5 }, bgcolor: COLORS.slate50, flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
          <InfoTile icon={<HeightRounded />} label="Boy *">
            {numericInput(height, setHeight, "cm")}
          </InfoTile>
          <InfoTile icon={<MonitorWeightRounded />} label="Kilo *">
            {numericInput(weight, setWeight, "kg")}
          </InfoTile>

          <InfoTile icon={<CalculateRounded />} label="Hesaplanan BMI" fullWidth>
            {bmi != null ? (
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
                <Typography sx={{ fontSize: 24, fontWeight: 600, color: COLORS.slate800 }}>
                  {String(bmi).replace(".", ",")}
                </Typography>
                {category ? (
                  <Typography
                    sx={{
                      fontSize: TYPO.body,
                      fontWeight: 500,
                      color: category.color || COLORS.slate500,
                    }}
                  >
                    {category.label}
                  </Typography>
                ) : null}
              </Box>
            ) : (
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate400 }}>
                {bothFilled ? "Geçerli bir boy (cm) ve kilo (kg) girin" : "—"}
              </Typography>
            )}
          </InfoTile>
        </Box>

        <Typography sx={{ mt: 1.5, fontSize: TYPO.label, color: COLORS.slate500 }}>
          BMI = kilo (kg) ÷ boy (m)². Boy ve kilo saklanmaz, yalnızca hesaplanan BMI kaydedilir.
        </Typography>
      </Box>

      <DrawerFooter
        onCancel={onClose}
        onSubmit={() => bmi != null && onSave(bmi)}
        submitLabel="Kaydet"
        loadingLabel="Kaydediliyor..."
        loading={saving}
        disabled={bmi == null}
        fullWidth
      />
    </Drawer>
  );
}
